/**
 * Skeletons de Staff (006 · T015, research R-19; design-system §6.2 "Skeletons, no spinners").
 *
 * - `Skeleton`: bloque Neutro 100 (`bg-fm-bg-sunken`) con el radio del contenido que imita,
 *   `aria-hidden` (no es contenido). El brillo (pulso de 1,2 s) solo corre sin
 *   `prefers-reduced-motion` (`motion-safe:`); con reducción de movimiento queda estático.
 * - `SkeletonText`: contenedor de carga accesible. Marca la región con `aria-busy="true"` y suma
 *   un texto `sr-only` ("Cargando…" por defecto) para que el lector de pantalla anuncie la espera;
 *   dentro van los bloques (`children`) o, si no se pasan, `lines` líneas de texto simuladas.
 *
 * `home-loading`, `history-loading`… son `data-testid` del contenedor (se pasan por props).
 */
import { t } from '../../i18n';

import { cn } from './cn';

import type { HTMLAttributes, ReactNode } from 'react';

export type SkeletonRadius = 'sm' | 'md' | 'lg' | 'pill';

const RADIUS_CLASSES: Record<SkeletonRadius, string> = {
  sm: 'rounded-fm-sm',
  md: 'rounded-fm-md',
  lg: 'rounded-fm-lg',
  pill: 'rounded-fm-pill',
};

/** Clases del brillo: solo sin reducción de movimiento, 1,2 s (R-19). */
export const SKELETON_SHIMMER_CLASSES = 'motion-safe:animate-pulse motion-safe:[animation-duration:1.2s]';

export interface SkeletonProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Radio del contenido que imita (por defecto `md`). */
  radius?: SkeletonRadius;
}

export function Skeleton({ radius = 'md', className, ...props }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      data-skeleton=""
      className={cn('bg-fm-bg-sunken', RADIUS_CLASSES[radius], SKELETON_SHIMMER_CLASSES, className)}
      {...props}
    />
  );
}

export interface SkeletonTextProps extends HTMLAttributes<HTMLDivElement> {
  /** Texto para el lector de pantalla; por defecto `common.states.loading` ("Cargando…"). */
  label?: string;
  /** Líneas simuladas cuando no se pasan `children` (por defecto 3). */
  lines?: number;
  children?: ReactNode;
}

export function SkeletonText({
  label,
  lines = 3,
  className,
  children,
  ...props
}: SkeletonTextProps) {
  return (
    <div aria-busy="true" className={cn('flex flex-col gap-fm-2', className)} {...props}>
      <span className="sr-only">{label ?? t('common.states.loading')}</span>
      {children ??
        Array.from({ length: lines }, (_, index) => (
          <Skeleton
            key={index}
            radius="sm"
            // La última línea es más corta, como un párrafo real.
            className={cn('h-4', index === lines - 1 && lines > 1 ? 'w-3/5' : 'w-full')}
          />
        ))}
    </div>
  );
}
